import React from 'react';

import { BooleanFieldValueRenderer } from '@/components/molecules/BooleanFieldValueRenderer';
import { CNPJFieldValueRenderer } from '@/components/molecules/CNPJFieldValueRenderer';
import { CompositeFieldValueRenderer } from '@/components/molecules/CompositeFieldValueRenderer';
import { DateFieldValueRenderer } from '@/components/molecules/DateFieldValueRenderer';
import { EmailFieldValueRenderer } from '@/components/molecules/EmailFieldValueRenderer';
import { EmptyFieldValueRenderer } from '@/components/molecules/EmptyFieldValueRenderer';
import { FileFieldValueRenderer } from '@/components/molecules/FileFieldValueRenderer';
import { MultipleValuesFieldValueRenderer } from '@/components/molecules/MultipleValuesFieldValueRenderer';
import { NumericFieldValueRenderer } from '@/components/molecules/NumericFieldValueRenderer';
import { PhoneNumberFieldValueRenderer } from '@/components/molecules/PhoneNumberFieldValueRenderer';
import { SelectFieldValueRenderer } from '@/components/molecules/SelectFieldValueRenderer';
import { TextFieldValueRenderer } from '@/components/molecules/TextFieldValueRenderer';
import { Field, FieldType, isValueType } from '@/types/fields';

interface Props {
  readonly value: unknown;
  readonly field: Field<unknown>;
}

export const FieldValueRenderer: React.FC<Props> = (props: Props): React.ReactElement => {
  const { value, field } = props;

  if (!isValueType(value)) {
    return <EmptyFieldValueRenderer />;
  }

  switch (field.type) {
    case FieldType.boolean:
      return <BooleanFieldValueRenderer value={value} field={field} />;
    case FieldType.text:
      return <TextFieldValueRenderer value={value} field={field} />;
    case FieldType.cnpj:
      return <CNPJFieldValueRenderer value={value} field={field} />;
    case FieldType.paragraph:
      return <TextFieldValueRenderer value={value} field={field} />;
    case FieldType.email:
      return <EmailFieldValueRenderer value={value} field={field} />;
    case FieldType.select:
      return <SelectFieldValueRenderer value={value} field={field} />;
    case FieldType.composite:
      return <CompositeFieldValueRenderer value={value} field={field} />;
    case FieldType.multipleValues:
      return <MultipleValuesFieldValueRenderer value={value} field={field} />;
    case FieldType.numeric:
      return <NumericFieldValueRenderer value={value} field={field} />;
    case FieldType.phoneNumber:
      return <PhoneNumberFieldValueRenderer value={value} field={field} />;
    case FieldType.file:
      return <FileFieldValueRenderer value={value} field={field} />;
    case FieldType.date:
      return <DateFieldValueRenderer value={value} field={field} />;
    case FieldType.embedded:
    case FieldType.list:
      throw new Error(
        'embedded and list fields are embedded or have embedded items, not rendered by their own',
      );
  }
};
